import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { chart, tooltipStyle } from './chartTheme';

/**
 * Donut with center total + direct-labelled legend. Ported from
 * D:\campaigning-system\src\components\charts\DonutChart.tsx.
 *
 * Color resolution: an explicit `colorFor` wins, else a status-named slice uses
 * the status color, else it falls back to the muted categorical `palette` by
 * index (the prototype fell back to a single teal, which flattens arbitrary
 * multi-slice data — this keeps slices distinguishable within the same restrained
 * palette). `unit` (e.g. '%') hides the redundant percent column when values are
 * already shares. `showTotal={false}` shows only the center label. `formatValue`
 * (e.g. rupees) replaces the plain number in the legend and tooltip.
 */
export function DonutChart({
  data,
  colorFor,
  centerLabel,
  centerValue,
  height = 200,
  unit = '',
  showTotal = true,
  formatValue,
}) {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  const color = (name, i) =>
    colorFor?.(name) ?? chart.status[name] ?? chart.palette[i % chart.palette.length];

  return (
    <div className="flex items-center gap-4">
      <div className="relative shrink-0" style={{ width: height, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="66%"
              outerRadius="94%"
              paddingAngle={2}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
            >
              {data.map((d, i) => (
                <Cell key={d.name} fill={color(d.name, i)} />
              ))}
            </Pie>
            <Tooltip
              {...tooltipStyle}
              formatter={(v, n) => [
                `${formatValue ? formatValue(v) : `${v}${unit}`} (${total ? Math.round((v / total) * 100) : 0}%)`,
                n,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {showTotal && (
            <span className="mono text-2xl font-bold leading-none text-cos-ink">
              {centerValue ?? total.toLocaleString('en-IN')}
            </span>
          )}
          {centerLabel && (
            <span className={`text-2xs font-medium uppercase tracking-wide text-cos-ink-muted ${showTotal ? 'mt-1' : ''}`}>
              {centerLabel}
            </span>
          )}
        </div>
      </div>

      {/* Legend with direct name + value (required relief for muted categorical palette) */}
      <ul className="min-w-0 flex-1 space-y-2">
        {data.map((d, i) => {
          const value = Number(d.value) || 0;
          const percent = total > 0 ? (value / total) * 100 : 0;
          return (
            <li key={d.name} className="flex items-center gap-2.5 text-sm">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color(d.name, i) }} />
              <span className="min-w-0 truncate text-cos-ink-secondary">{d.name}</span>
              <span className="mono ml-auto font-semibold text-cos-ink">
                {formatValue ? formatValue(value) : `${value.toLocaleString('en-IN')}${unit}`}
              </span>
              {!unit && (
                <span className="mono w-9 text-right text-2xs text-cos-ink-muted">{percent.toFixed(0)}%</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default DonutChart;
